import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  FormControlLabel,
  Checkbox,
  Box,
  FormHelperText,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from "@mui/material";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import AddIcon from "@mui/icons-material/Add";
import { DatePicker } from "@mui/x-date-pickers";
import { parse, isValid, format } from "date-fns";

import { darkDatePickerSlotProps } from "../../../../themes/datePickerStyles";
import { ComponentType, PendingComponentSubAssembly } from "../../../../types/component";
import { ComponentMetadata } from "../../../../types/componentMetadataConfig";
import {
  normalizeOffWingTypeForForm,
  OFF_WING_TYPE_OPTIONS,
} from "../../../../utils/offWingType";
import { useShopVisitReasonService } from "../../../../api/shopVisitReasonService";
import { ShopVisitReasonComponentType, ShopVisitReasonItem } from "../../../../types/shopVisitReason";
import { useTenantDisplayPreferences } from "../../../../hooks/useTenantDisplayPreferences";
import { useOperatorService } from "../../../../api/operatorService";
import { OwnershipFormFields } from "../../../common/OwnershipFormFields";
import { normalizeOwnershipStatus, normalizeOperatorIdForForm } from "../../../../constants/ownershipStatus";
import { normalizeOperatorsFromApi } from "../../../../utils/operatorUtils";
import { shouldShowComponentOwnershipFields } from "../../../../utils/ownershipVisibility";
import { Operator } from "../../../../types/operator";
import { DisplayPreferences } from "../../../../hooks/useTenantDisplayPreferences";
import { useAirframeFamilies } from "../hooks/useSubAssemblyCatalog";
import {
  filterSupportedAirframeFamilies,
  normalizeAirframeFamily,
} from "../hooks/useSubAssemblyCatalog";
import {
  buildLandingGearSerialFromSubAssemblies,
  prepareComponentMetadataForSave,
} from "../../../../utils/landingGearAttachUtils";
import { validateComponentForm, LANDING_GEAR_SUB_ASSEMBLY_REQUIRED_MESSAGE, getPersistedSubAssemblyDetachBlockReason } from "../../../../utils/componentFormValidation";
import { ValidationErrorsDialog } from "../../../common/validation-errors-dialog";
import { AttachSubAssemblyDialog } from "./AttachSubAssemblyDialog";

type FormSnapshot = {
  manufacturer: string;
  model: string;
  dateManufactured: string;
  ownershipStatus: string | null;
  operatorId: string | null;
  owner: string;
  customMetadata: Record<string, unknown>;
  pendingSubAssemblies: Array<{
    id: string;
    landingGearType: string;
    subAssemblyType: string;
    serialNumber: string;
    partNumber?: string;
    isPersisted?: boolean;
  }>;
};

function normalizeDateValue(value: unknown): string {
  if (!value) return "";
  const raw = value instanceof Date ? format(value, "yyyy-MM-dd") : String(value);
  return raw.includes("T") ? raw.split("T")[0] : raw;
}

function buildFormSnapshot(
  form: any,
  pendingSubAssemblies: PendingComponentSubAssembly[]
): FormSnapshot {
  const customMetadata = { ...(form.customMetadata ?? {}) };
  delete customMetadata.lgHasSubAssembly;

  return {
    manufacturer: String(form.manufacturer ?? "").trim(),
    model: String(form.model ?? "").trim(),
    dateManufactured: normalizeDateValue(form.dateManufactured),
    ownershipStatus: form.ownershipStatus ?? null,
    operatorId: form.operatorId ?? null,
    owner: String(form.owner ?? "").trim(),
    customMetadata,
    pendingSubAssemblies: pendingSubAssemblies
      .map((item) => ({
        id: item.id,
        landingGearType: item.landingGearType,
        subAssemblyType: item.subAssemblyType,
        serialNumber: item.serialNumber,
        partNumber: item.partNumber ?? undefined,
        isPersisted: item.isPersisted,
      }))
      .sort((left, right) => left.id.localeCompare(right.id)),
  };
}

type Props = {
  open: boolean;
  form: any;
  isCreate: boolean;
  change: (k: any, v: any) => void;
  changeMetadata: (key: string, value: any) => void;
  close: () => void;
  onSave: (v: any) => void;
  hasSubAssembly?: boolean;
  onHasSubAssemblyChange?: (checked: boolean) => void;
  pendingSubAssemblies?: PendingComponentSubAssembly[];
  onAddPendingSubAssembly?: (item: PendingComponentSubAssembly) => void;
  onRemovePendingSubAssembly?: (subAssemblyId: string, isPersisted?: boolean) => void;
  displayPreferences?: DisplayPreferences;
  operators?: Operator[];
  formBaselineKey?: number;
};

export function ComponentFormDialog({
  open,
  changeMetadata,
  form,
  isCreate,
  change,
  close,
  onSave,
  hasSubAssembly = false,
  onHasSubAssemblyChange,
  pendingSubAssemblies = [],
  onAddPendingSubAssembly,
  onRemovePendingSubAssembly,
  displayPreferences: displayPreferencesProp,
  operators: operatorsProp,
  formBaselineKey = 0,
}: Readonly<Props>) {
  const [isSaving, setIsSaving] = useState(false);
  const [reasonOptions, setReasonOptions] = useState<ShopVisitReasonItem[]>([]);
  const [reasonsLoading, setReasonsLoading] = useState(false);
  const [attachSubAssemblyOpen, setAttachSubAssemblyOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [subAssemblyError, setSubAssemblyError] = useState(false);
  const [unsavedChangesOpen, setUnsavedChangesOpen] = useState(false);
  const initialSnapshotRef = useRef<string | null>(null);
  const internalPreferences = useTenantDisplayPreferences(
    open && displayPreferencesProp === undefined
  );
  const displayPreferences = displayPreferencesProp ?? internalPreferences.preferences;
  const { getOperators } = useOperatorService();
  const [internalOperators, setInternalOperators] = useState<Operator[]>([]);
  const [internalOperatorsLoading, setInternalOperatorsLoading] = useState(false);
  const operators = operatorsProp ?? internalOperators;
  const operatorsLoading =
    operatorsProp !== undefined ? false : internalOperatorsLoading;
  const { getReasons } = useShopVisitReasonService();
  const { families, loading: familiesLoading } = useAirframeFamilies();
  const airframeFamily = normalizeAirframeFamily(
    String(form.customMetadata?.lgAirframeFamily ?? "")
  );

  const supportedFamilies = useMemo(() => {
    return filterSupportedAirframeFamilies([
      ...families,
      ...(airframeFamily ? [airframeFamily] : []),
    ]);
  }, [families, airframeFamily]);

  const isLandingGear = form.type === "LandingGear";
  const isSerialNumberAutoGenerated = isLandingGear && hasSubAssembly;
  const isAirframeFamilyLocked = pendingSubAssemblies.length > 0;
  const resolvedSerialNumber = isSerialNumberAutoGenerated
    ? buildLandingGearSerialFromSubAssemblies(pendingSubAssemblies)
    : form.serialNumber || "";

  useEffect(() => {
    if (!open) {
      initialSnapshotRef.current = null;
      setUnsavedChangesOpen(false);
      return;
    }

    if (isCreate) {
      initialSnapshotRef.current = null;
      return;
    }

    initialSnapshotRef.current = JSON.stringify(
      buildFormSnapshot(form, pendingSubAssemblies)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isCreate, formBaselineKey]);

  const isDirty = useMemo(() => {
    if (!open || isCreate || !initialSnapshotRef.current) return false;

    const currentSnapshot = JSON.stringify(
      buildFormSnapshot(form, pendingSubAssemblies)
    );
    return currentSnapshot !== initialSnapshotRef.current;
  }, [open, isCreate, form, pendingSubAssemblies]);

  const shouldBlockClose =
    !isCreate && isLandingGear && hasSubAssembly && isDirty;

  const handleRequestClose = () => {
    if (shouldBlockClose) {
      setUnsavedChangesOpen(true);
      return;
    }
    close();
  };

  // Helper to parse date string to Date object without timezone issues
  const parseDate = (dateStr: any): Date | null => {
    if (!dateStr) return null;
    if (dateStr instanceof Date) return dateStr;
    if (typeof dateStr === 'string') {
      // Try multiple formats
      const formats = ['yyyy-MM-dd', 'yyyy-MM-dd HH:mm:ss', 'yyyy-MM-dd\'T\'HH:mm:ss'];
      for (const fmt of formats) {
        try {
          const parsed = parse(dateStr, fmt, new Date());
          if (isValid(parsed)) return parsed;
        } catch {
          // Continue to next format
        }
      }
      // If no format matched, try extracting just the date part if it contains 'T'
      if (dateStr.includes('T')) {
        const dateOnly = dateStr.split('T')[0];
        try {
          const parsed = parse(dateOnly, 'yyyy-MM-dd', new Date());
          if (isValid(parsed)) return parsed;
        } catch {
          // Ignore
        }
      }
      return null;
    }
    return null;
  };

  const isAttached = Boolean(
    (form.attachedToAirframe && String(form.attachedToAirframe).trim()) &&
      (form.attachmentLocation && String(form.attachmentLocation).trim())
  );

  const showOwnershipFields = shouldShowComponentOwnershipFields(
    form.attachedToAirframe,
    form.attachmentLocation
  );

  const offWingType =
    isLandingGear && (hasSubAssembly || pendingSubAssemblies.length > 0)
      ? ""
      : normalizeOffWingTypeForForm(form.customMetadata?.offWingType);
  const showOffWingDetails =
    !isAttached &&
    !(isLandingGear && (hasSubAssembly || pendingSubAssemblies.length > 0));
  const shouldShowReason = showOffWingDetails && offWingType === "In Shop";

  const save = async () => {
    if (isSaving) return; // Prevent multiple concurrent saves

    const customMetadataForSave = prepareComponentMetadataForSave(
      form.customMetadata,
      {
        isLandingGear,
        hasSubAssembly: hasSubAssembly || pendingSubAssemblies.length > 0,
      }
    );

    const errors = validateComponentForm({
      serialNumber: resolvedSerialNumber,
      manufacturer: form.manufacturer,
      model: form.model,
      isAttached,
      offWingType,
      shouldShowReason,
      shopVisitReasonId:
        isLandingGear && (hasSubAssembly || pendingSubAssemblies.length > 0)
          ? ""
          : String(customMetadataForSave.shopVisitReasonId ?? ""),
      isLandingGear,
      hasSubAssemblyChecked: hasSubAssembly,
      hasSubAssembly: hasSubAssembly || pendingSubAssemblies.length > 0,
      pendingSubAssemblyCount: pendingSubAssemblies.length,
      pendingSubAssemblyLandingGearTypes: pendingSubAssemblies.map(
        (item) => item.landingGearType
      ),
      pendingSubAssemblyTypes: pendingSubAssemblies.map(
        (item) => item.subAssemblyType
      ),
    });

    if (isLandingGear && hasSubAssembly && !airframeFamily) {
      errors.push("Airframe type is required when landing gear has sub-assemblies.");
    }

    if (errors.length > 0) {
      setSubAssemblyError(
        errors.includes(LANDING_GEAR_SUB_ASSEMBLY_REQUIRED_MESSAGE)
      );
      setValidationErrors(errors);
      return;
    }

    setSubAssemblyError(false);
    
    setIsSaving(true);
    try {
      await onSave({
        ...form,
        serialNumber: resolvedSerialNumber,
        hasSubAssembly,
        pendingSubAssemblies,
        customMetadata: customMetadataForSave,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const getReasonComponentType = (type: string): ShopVisitReasonComponentType | null => {
    if (type === "Engine" || type === "APU" || type === "LandingGear") {
      return type;
    }
    if (type === "Landing Gear") {
      return "LandingGear";
    }
    return null;
  };

  useEffect(() => {
    if (!open || !shouldShowReason) {
      setReasonOptions([]);
      setReasonsLoading(false);
      return;
    }

    const componentType = getReasonComponentType(form.type || "");
    if (!componentType) {
      setReasonOptions([]);
      return;
    }

    let active = true;
    setReasonsLoading(true);
    void getReasons(componentType)
      .then((reasons) => {
        if (active) {
          setReasonOptions(Array.isArray(reasons) ? reasons : []);
        }
      })
      .catch(() => {
        if (active) {
          setReasonOptions([]);
        }
      })
      .finally(() => {
        if (active) {
          setReasonsLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [open, shouldShowReason, form.type, getReasons]);

  useEffect(() => {
    if (!open) {
      setValidationErrors([]);
      setSubAssemblyError(false);
    }
  }, [open]);

  useEffect(() => {
    if (pendingSubAssemblies.length > 0) {
      setSubAssemblyError(false);
    }
  }, [pendingSubAssemblies.length]);

  useEffect(() => {
    if (
      operatorsProp !== undefined ||
      !open ||
      !showOwnershipFields ||
      !displayPreferences.showOperator
    ) {
      if (operatorsProp === undefined) {
        setInternalOperators([]);
        setInternalOperatorsLoading(false);
      }
      return;
    }

    let active = true;
    setInternalOperatorsLoading(true);
    void getOperators(true)
      .then((items) => {
        if (active) setInternalOperators(normalizeOperatorsFromApi(items));
      })
      .catch(() => {
        if (active) setInternalOperators([]);
      })
      .finally(() => {
        if (active) setInternalOperatorsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [
    open,
    getOperators,
    showOwnershipFields,
    displayPreferences.showOperator,
    operatorsProp,
  ]);

  return (
    <Dialog
      open={open}
      onClose={() => handleRequestClose()}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "#111",
          color: "#fff",
          border: "1px solid #333",
        },
      }}
    >
      <DialogTitle sx={{ color: "hsl(19 101% 51% / 1)" }}>
        {isCreate ? "Create" : "Edit"} Assembly
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth required disabled={!isCreate}>
              <InputLabel 
                sx={{ 
                  color: "#fff",
                  "& .MuiInputLabel-asterisk": {
                    color: "hsl(19 101% 51% / 1)",
                  },
                }}
              >
                Type
              </InputLabel>
              <Select
                value={form.type || ""}
                onChange={(e) => change("type", e.target.value as ComponentType)}
                sx={{
                  color: "#fff",
                  backgroundColor: "#1a1a1a",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#333",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "hsl(19 101% 51% / 1)",
                  },
                  "& .MuiSelect-icon": {
                    color: "#fff",
                  },
                  "& .MuiOutlinedInput-input": {
                    color: "#fff !important",
                    backgroundColor: "#1a1a1a",
                    padding: "12px 14px",
                  },
                  "&.Mui-disabled": {
                    color: "#fff",
                    backgroundColor: "#1a1a1a",
                    "& .MuiOutlinedInput-input": {
                      color: "#fff !important",
                      WebkitTextFillColor: "#fff !important",
                      backgroundColor: "#1a1a1a",
                    },
                    "& .MuiSelect-icon": {
                      color: "#fff",
                    },
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: "#222",
                      color: "#fff",
                      "& .MuiMenuItem-root": {
                        color: "#fff",
                        "&:hover": {
                          backgroundColor: "hsl(19 101% 51% / 0.2)",
                        },
                        "&.Mui-selected": {
                          backgroundColor: "hsl(19 101% 51% / 0.3)",
                        },
                      },
                    },
                  },
                }}
              >
                <MenuItem value="Engine">Engine</MenuItem>
                <MenuItem value="APU">APU</MenuItem>
                <MenuItem value="LandingGear">LandingGear</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {isLandingGear && (
            <Grid
              size={{ xs: 12, sm: 6 }}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hasSubAssembly}
                    disabled={!isCreate}
                    onChange={(e) => onHasSubAssemblyChange?.(e.target.checked)}
                    sx={{
                      color: "#888",
                      "&.Mui-checked": { color: "hsl(19 101% 51% / 1)" },
                    }}
                  />
                }
                label={
                  <Typography sx={{ color: "#fff" }}>
                    Landing gear has sub-assembly
                  </Typography>
                }
              />
            </Grid>
          )}

          {["serialNumber", "manufacturer", "model"].map((f) => (
            <Grid size={{ xs: 12, sm: 6 }} key={f}>
              <TextField
                fullWidth
                required
                disabled={
                  f === "serialNumber" &&
                  (isSerialNumberAutoGenerated || !isCreate)
                }
                InputProps={
                  f === "serialNumber" && !isCreate
                    ? { readOnly: true }
                    : undefined
                }
                label={f === "serialNumber" ? "Serial Number" : f.charAt(0).toUpperCase() + f.slice(1)}
                value={f === "serialNumber" ? resolvedSerialNumber : form[f] || ""}
                onChange={(e) => change(f, e.target.value)}
                sx={{
                  "& .MuiInputLabel-root": { 
                    color: "#fff",
                    "& .MuiInputLabel-asterisk": {
                      color: "hsl(19 101% 51% / 1)",
                    },
                  },
                  "& .MuiOutlinedInput-root": {
                    color: "#fff",
                    "& fieldset": { borderColor: "#333" },
                    "&:hover fieldset": {
                      borderColor: "hsl(19 101% 51% / 1)",
                    },
                    "&.Mui-disabled": {
                      color: "#fff",
                      "& fieldset": { borderColor: "#555" },
                    },
                  },
                  "& .MuiInputBase-input.Mui-disabled": {
                    color: "#fff",
                    WebkitTextFillColor: "#fff",
                    opacity: 1,
                  },
                }}
              />
            </Grid>
          ))}

          <Grid size={{ xs: 12, sm: 6 }}>
            <DatePicker
              label="Date Manufactured"
              format="dd/MM/yyyy"
              value={parseDate(form.dateManufactured)}
              onChange={(d) =>
                change("dateManufactured", d ? format(d, "yyyy-MM-dd") : "")
              }
              slotProps={{
                ...darkDatePickerSlotProps,
                textField: darkDatePickerSlotProps.textField,
              }}
            />
          </Grid>

          {showOwnershipFields && (
            <Grid size={{ xs: 12 }}>
              <OwnershipFormFields
                preferences={displayPreferences}
                ownershipStatus={normalizeOwnershipStatus(form.ownershipStatus)}
                owner={form.owner ?? ""}
                operatorId={normalizeOperatorIdForForm(form.operatorId)}
                operatorName={form.operatorName ?? null}
                operators={operators}
                operatorsLoading={operatorsLoading}
                showOperator
                onOwnershipStatusChange={(v) =>
                  change("ownershipStatus", v === "" ? null : v)
                }
                onOwnerChange={(v) => change("owner", v)}
                onOperatorIdChange={(v) =>
                  change("operatorId", v ? normalizeOperatorIdForForm(v) : null)
                }
                hideOwner
              />
            </Grid>
          )}

          {showOffWingDetails && (
            <>
              <Grid size={{ xs: 12 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: "hsl(19 101% 51% / 1)",
                    fontWeight: "bold",
                    mt: 2,
                    mb: 1,
                  }}
                >
                  OffWing Details
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth required>
                  <InputLabel
                    required
                    sx={{
                      color: "#fff",
                      "&.Mui-focused": { color: "#fff" },
                      "&.MuiInputLabel-shrink": { color: "#fff" },
                      "& .MuiInputLabel-asterisk": {
                        color: "hsl(19 101% 51% / 1)",
                      },
                    }}
                  >
                    OffWing Type
                  </InputLabel>
                  <Select
                    label="OffWing Type"
                    value={offWingType}
                    onChange={(e) => {
                      const nextOffWingType = e.target.value;
                      changeMetadata("offWingType", nextOffWingType);
                      if (nextOffWingType !== "In Shop") {
                        changeMetadata("shopVisitReasonId", "");
                        changeMetadata("shopVisitReasonLabel", "");
                      }
                    }}
                    sx={{
                      color: "#fff",
                      backgroundColor: "#1a1a1a",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#fff",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#fff",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "hsl(19 101% 51% / 1)",
                        borderWidth: 1,
                      },
                      "& .MuiSelect-icon": {
                        color: "#fff",
                      },
                      "& .MuiOutlinedInput-input": {
                        color: "#fff !important",
                        backgroundColor: "#1a1a1a",
                        padding: "12px 14px",
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          backgroundColor: "#222",
                          color: "#fff",
                          "& .MuiMenuItem-root": {
                            color: "#fff",
                            "&:hover": {
                              backgroundColor: "hsl(19 101% 51% / 0.2)",
                            },
                            "&.Mui-selected": {
                              backgroundColor: "hsl(19 101% 51% / 0.3)",
                            },
                          },
                        },
                      },
                    }}
                  >
                    {OFF_WING_TYPE_OPTIONS.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {shouldShowReason && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth required>
                    <InputLabel
                      required
                      sx={{
                        color: "#fff",
                        "&.Mui-focused": { color: "#fff" },
                        "&.MuiInputLabel-shrink": { color: "#fff" },
                        "& .MuiInputLabel-asterisk": {
                          color: "hsl(19 101% 51% / 1)",
                        },
                      }}
                    >
                      Inshop Reasons
                    </InputLabel>
                    <Select
                      label="Inshop Reasons"
                      value={form.customMetadata?.shopVisitReasonId || ""}
                      onChange={(e) => {
                        const selectedId = String(e.target.value || "");
                        const selectedReason = reasonOptions.find((item) => item.id === selectedId);
                        changeMetadata("shopVisitReasonId", selectedId);
                        changeMetadata("shopVisitReasonLabel", selectedReason?.label || "");
                      }}
                      disabled={reasonsLoading}
                      sx={{
                        color: "#fff",
                        backgroundColor: "#1a1a1a",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#fff",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#fff",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "hsl(19 101% 51% / 1)",
                          borderWidth: 1,
                        },
                        "& .MuiSelect-icon": {
                          color: "#fff",
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            backgroundColor: "#222",
                            color: "#fff",
                            "& .MuiMenuItem-root": {
                              color: "#fff",
                              "&:hover": {
                                backgroundColor: "hsl(19 101% 51% / 0.2)",
                              },
                              "&.Mui-selected": {
                                backgroundColor: "hsl(19 101% 51% / 0.3)",
                              },
                            },
                          },
                        },
                      }}
                    >
                      {reasonOptions.map((opt, index) => (
                        <MenuItem key={`${String(opt.id)}-${opt.label}-${index}`} value={opt.id}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </>
          )}

          {/* Dynamic Metadata Fields */}
          {(() => {
            const fields = (ComponentMetadata[form.type || ""] || []).filter(
              (field) => !(isLandingGear && field.key === "lgHasSubAssembly")
            );
            const groups = fields.reduce((acc, field) => {
              const section = field.section || "Other";
              if (!acc[section]) acc[section] = [];
              acc[section].push(field);
              return acc;
            }, {} as Record<string, typeof fields>);

            return Object.entries(groups)
              .filter(([, sectionFields]) => sectionFields.length > 0)
              .map(([sectionName, sectionFields]) => (
              <React.Fragment key={sectionName}>
                <Grid size={{ xs: 12 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: "hsl(19 101% 51% / 1)",
                      fontWeight: "bold",
                      mt: 2,
                      mb: 1,
                    }}
                  >
                    {sectionName}
                  </Typography>
                </Grid>
                {sectionFields.map((field) => (
                  <Grid size={{ xs: 12, sm: 6 }} key={field.key}>
                    {field.type === "text" && (
                      <TextField
                        fullWidth
                        label={field.label}
                        required={field.required}
                        value={form.customMetadata?.[field.key] || ""}
                        onChange={(e) =>
                          changeMetadata(field.key, e.target.value)
                        }
                        sx={{
                          "& .MuiInputLabel-root": { 
                            color: "#fff",
                            "& .MuiInputLabel-asterisk": {
                              color: "hsl(19 101% 51% / 1)",
                            },
                          },
                          "& .MuiOutlinedInput-root": {
                            color: "#fff",
                            "& fieldset": { borderColor: "#333" },
                            "&:hover fieldset": {
                              borderColor: "hsl(19 101% 51% / 1)",
                            },
                          },
                        }}
                      />
                    )}
                    {field.type === "number" && (
                      <TextField
                        fullWidth
                        type="number"
                        label={field.label}
                        required={field.required}
                        value={form.customMetadata?.[field.key] ?? ""}
                        onChange={(e) =>
                          changeMetadata(field.key, e.target.value)
                        }
                        sx={{
                          "& .MuiInputLabel-root": { 
                            color: "#fff",
                            "& .MuiInputLabel-asterisk": {
                              color: "hsl(19 101% 51% / 1)",
                            },
                          },
                          "& .MuiOutlinedInput-root": {
                            color: "#fff",
                            "& fieldset": { borderColor: "#333" },
                            "&:hover fieldset": {
                              borderColor: "hsl(19 101% 51% / 1)",
                            },
                          },
                        }}
                      />
                    )}
                    {field.type === "date" && (
                      <DatePicker
                        label={field.label}
                        format="dd/MM/yyyy"
                        value={parseDate(form.customMetadata?.[field.key])}
                        onChange={(d) =>
                          changeMetadata(
                            field.key,
                            d ? format(d, "yyyy-MM-dd") : ""
                          )
                        }
                        slotProps={{
                          ...darkDatePickerSlotProps,
                          textField: {
                            ...darkDatePickerSlotProps.textField,
                            required: field.required,
                          },
                        }}
                      />
                    )}
                    {field.type === "select" && (
                      <FormControl
                        fullWidth
                        required={field.required}
                        sx={{
                          "& .MuiInputLabel-root": { 
                            color: "#fff",
                            "& .MuiInputLabel-asterisk": {
                              color: "hsl(19 101% 51% / 1)",
                            },
                          },
                          "& .MuiOutlinedInput-root": {
                            color: "#fff",
                            "& fieldset": { borderColor: "#333" },
                            "&:hover fieldset": {
                              borderColor: "hsl(19 101% 51% / 1)",
                            },
                          },
                        }}
                      >
                        <InputLabel required={field.required}>{field.label}</InputLabel>
                        <Select
                          value={form.customMetadata?.[field.key] || ""}
                          onChange={(e) =>
                            changeMetadata(field.key, e.target.value)
                          }
                          label={field.label}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                backgroundColor: "#222",
                                color: "#fff",
                                "& .MuiMenuItem-root": {
                                  color: "#fff",
                                  "&:hover": {
                                    backgroundColor: "hsl(19 101% 51% / 0.2)",
                                  },
                                  "&.Mui-selected": {
                                    backgroundColor: "hsl(19 101% 51% / 0.3)",
                                  },
                                },
                              },
                            },
                          }}
                        >
                          {(field.options || []).map((opt) => (
                            <MenuItem key={opt} value={opt}>
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Grid>
                ))}
              </React.Fragment>
            ));
          })()}

          {isLandingGear && hasSubAssembly && (
            <>
              <Grid size={{ xs: 12 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: "hsl(19 101% 51% / 1)",
                    fontWeight: "bold",
                    mt: 2,
                    mb: 1,
                  }}
                >
                  Sub-Assembly
                </Typography>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                    mb: 1,
                  }}
                >
                  <FormControl
                    size="small"
                    sx={{ minWidth: 220 }}
                    disabled={familiesLoading}
                  >
                    <InputLabel
                      sx={{
                        color: "#bbb",
                        "&.Mui-disabled": {
                          color: "#bbb",
                        },
                        lineHeight: 1.2,
                        overflow: "visible",
                        "&.MuiInputLabel-shrink": {
                          backgroundColor: "#111",
                          paddingInline: "4px",
                          transform: "translate(14px, -9px) scale(0.75)",
                        },
                      }}
                    >
                      Airframe Type
                    </InputLabel>
                    <Select
                      value={airframeFamily}
                      label="Airframe Type"
                      onChange={(e) =>
                        changeMetadata(
                          "lgAirframeFamily",
                          normalizeAirframeFamily(e.target.value)
                        )
                      }
                      disabled={familiesLoading || isAirframeFamilyLocked}
                      sx={{
                        color: "#fff",
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#555" },
                        "& .MuiSvgIcon-root": { color: "#fff" },
                        ...(isAirframeFamilyLocked
                          ? {
                              "&.Mui-disabled": {
                                color: "#fff",
                                opacity: 1,
                                WebkitTextFillColor: "#fff",
                                backgroundColor: "#1a1a1a",
                              },
                              "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#555",
                              },
                              "& .MuiSelect-select.Mui-disabled": {
                                color: "#fff",
                                WebkitTextFillColor: "#fff",
                                opacity: 1,
                              },
                              "& .MuiSvgIcon-root.Mui-disabled": {
                                color: "#fff",
                                opacity: 1,
                              },
                            }
                          : {}),
                      }}
                    >
                      {supportedFamilies.map((family) => (
                        <MenuItem key={family} value={family}>
                          {family}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setAttachSubAssemblyOpen(true)}
                    disabled={!airframeFamily}
                    sx={{
                      color: "hsl(19 101% 51% / 1)",
                      borderColor: "hsl(19 101% 51% / 1)",
                      textTransform: "none",
                      "&:hover": {
                        borderColor: "hsl(19 101% 41% / 1)",
                        backgroundColor: "hsl(19 101% 51% / 0.08)",
                      },
                    }}
                  >
                    Attach Sub-Assembly
                  </Button>
                </Box>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Box
                      sx={{
                        border: subAssemblyError
                          ? "1px solid #f44336"
                          : "1px solid #333",
                        borderRadius: 1,
                        p: 1.5,
                        backgroundColor: "#1a1a1a",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 1,
                        }}
                      >
                        <Typography sx={{ color: "#aaa", fontSize: "0.85rem" }}>
                          Attached sub-assemblies (saved when you create/update)
                        </Typography>
                      </Box>

                      {pendingSubAssemblies.length === 0 ? (
                        <Typography sx={{ color: "#888", fontSize: "0.85rem" }}>
                          No sub-assemblies attached yet.
                        </Typography>
                      ) : (
                        <List dense disablePadding>
                          {pendingSubAssemblies.map((item) => {
                            const removeLabel =
                              isCreate || !item.isPersisted
                                ? "Remove sub-assembly"
                                : "Detach sub-assembly";
                            const detachBlockReason =
                              getPersistedSubAssemblyDetachBlockReason(
                                pendingSubAssemblies,
                                {
                                  isCreate,
                                  isPersisted: Boolean(item.isPersisted),
                                }
                              );
                            return (
                            <ListItem key={item.id} sx={{ px: 0, py: 0.25 }}>
                              <ListItemText
                                primary={
                                  <Typography sx={{ color: "#fff", fontSize: "0.85rem" }}>
                                    {item.landingGearType} — {item.subAssemblyType} — S/N:{" "}
                                    {item.serialNumber}
                                    {item.partNumber ? ` (PN: ${item.partNumber})` : ""}
                                  </Typography>
                                }
                              />
                              <IconButton
                                size="small"
                                aria-label={removeLabel}
                                title={detachBlockReason ?? removeLabel}
                                disabled={Boolean(detachBlockReason)}
                                sx={{
                                  color: "hsl(19 101% 51% / 1)",
                                  "&.Mui-disabled": {
                                    color: "hsl(19 101% 51% / 0.4)",
                                  },
                                }}
                                onClick={() =>
                                  onRemovePendingSubAssembly?.(
                                    item.id,
                                    Boolean(item.isPersisted)
                                  )
                                }
                              >
                                <LinkOffIcon fontSize="small" />
                              </IconButton>
                            </ListItem>
                            );
                          })}
                        </List>
                      )}
                      {subAssemblyError && (
                        <FormHelperText error sx={{ mt: 1, mx: 0 }}>
                          {LANDING_GEAR_SUB_ASSEMBLY_REQUIRED_MESSAGE}
                        </FormHelperText>
                      )}
                    </Box>
              </Grid>
            </>
          )}

          {isAttached && (
            <>
              <Grid size={{ xs: 12 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: "hsl(19 101% 51% / 1)",
                    fontWeight: "bold",
                    mt: 2,
                    mb: 1,
                  }}
                >
                  Attachment Info
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  disabled
                  label="Attached To"
                  value={form.attachedToAirframe || ""}
                  sx={{
                    "& .MuiInputLabel-root": {
                      color: "#999",
                    },
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#1a1a1a",
                      color: "#fff",
                      "& fieldset": { borderColor: "#333" },
                      "& input": {
                        color: "#fff",
                        WebkitTextFillColor: "#fff",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "#1a1a1a",
                        "& fieldset": { borderColor: "#333" },
                        color: "#fff",
                        "& input": {
                          color: "#fff",
                          WebkitTextFillColor: "#fff",
                        },
                      },
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  disabled
                  label="Position"
                  value={form.attachmentLocation || ""}
                  sx={{
                    "& .MuiInputLabel-root": {
                      color: "#999",
                    },
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#1a1a1a",
                      color: "#fff",
                      "& fieldset": { borderColor: "#333" },
                      "& input": {
                        color: "#fff",
                        WebkitTextFillColor: "#fff",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "#1a1a1a",
                        "& fieldset": { borderColor: "#333" },
                        color: "#fff",
                        "& input": {
                          color: "#fff",
                          WebkitTextFillColor: "#fff",
                        },
                      },
                    },
                  }}
                />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="outlined"
          onClick={handleRequestClose}
          sx={{
            color: "hsl(19 101% 51% / 1)",
            borderColor: "hsl(19 101% 51% / 1)",
            textTransform: "none",
            "&:hover": {
              borderColor: "hsl(19 101% 41% / 1)",
              backgroundColor: "hsl(19 101% 51% / 0.08)",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => save()}
          disabled={isSaving}
          sx={{
            backgroundColor: "hsl(19 101% 51% / 1)",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "hsl(19 101% 41% / 1)",
            },
            "&:disabled": {
              backgroundColor: "hsl(19 101% 51% / 0.4)",
              color: "#fff",
            },
          }}
        >
          {isSaving ? "Saving..." : isCreate ? "Create" : "Update"}
        </Button>
      </DialogActions>

      <Dialog
        open={unsavedChangesOpen}
        onClose={() => setUnsavedChangesOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#111",
            color: "#fff",
            border: "1px solid #333",
          },
        }}
      >
        <DialogTitle sx={{ color: "hsl(19 101% 51% / 1)" }}>
          Unsaved Changes
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#ccc", mt: 1 }}>
            You have unsaved changes. Please save to continue.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setUnsavedChangesOpen(false)}
            variant="contained"
            sx={{
              backgroundColor: "hsl(19 101% 51% / 1)",
              "&:hover": { backgroundColor: "hsl(19 101% 41% / 1)" },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <AttachSubAssemblyDialog
        open={attachSubAssemblyOpen}
        defaultAirframeFamily={airframeFamily}
        pendingSubAssemblies={pendingSubAssemblies}
        onClose={() => setAttachSubAssemblyOpen(false)}
        onAttach={(item) => onAddPendingSubAssembly?.(item)}
      />

      <ValidationErrorsDialog
        open={validationErrors.length > 0}
        errors={validationErrors}
        onClose={() => setValidationErrors([])}
      />
    </Dialog>
  );
}
